/**
 * useScrollBookmark — restores and persists a user's scroll position
 * within a scrollable container, keyed by (conversationId, tabId).
 *
 * Behavior:
 * - On mount / conversationId change: restore from localStorage if a bookmark
 *   exists and its anchor is still in the DOM; otherwise apply `firstVisit`.
 * - On scroll (debounced 80ms): detect the top-most visible anchor and save.
 *
 * We deliberately do NOT save on cleanup — the short debounce captures scroll
 * positions fast enough, and cleanup-save caused pre-restore overwrites under
 * React StrictMode's double-invoke. Any scroll within the last 80ms is lost on
 * a tab-switch, which in practice means "none" because the switch click alone
 * adds more delay.
 *
 * Anchor candidates are elements matching `[data-bookmark-anchor]` inside the
 * container; each must have a unique `id` attribute. The tab component is
 * responsible for tagging them.
 */

import { RefObject, useEffect, useRef } from 'react'
import {
  BookmarkTabId,
  getBookmark,
  saveBookmark,
} from './scrollBookmarkStorage'

const SCROLL_DEBOUNCE_MS = 80
const ANCHOR_SELECTOR = '[data-bookmark-anchor]'

interface UseScrollBookmarkOptions {
  containerRef: RefObject<HTMLElement>
  conversationId: string | null
  tabId: BookmarkTabId
  ready: boolean
  firstVisit?: 'top' | 'bottom'
}

interface AnchorSnapshot {
  id: string
  offset: number
}

/**
 * Identify the anchor the user is currently viewing as the "top" of their view.
 *
 * Returns the anchor whose top edge is at or just above the container's top
 * (largest non-positive offset), or the first below-top anchor if none are at
 * or above it. Offset is negative when the anchor has scrolled above the
 * container top — restoring that offset reproduces the scroll position inside
 * a long section.
 */
function findTopAnchor(container: HTMLElement): AnchorSnapshot | null {
  const anchors = container.querySelectorAll<HTMLElement>(ANCHOR_SELECTOR)
  if (anchors.length === 0) return null

  const containerTop = container.getBoundingClientRect().top
  let bestAbove: { el: HTMLElement; offset: number } | null = null
  let firstBelow: { el: HTMLElement; offset: number } | null = null

  for (const el of anchors) {
    const offset = el.getBoundingClientRect().top - containerTop
    if (offset <= 0) {
      if (!bestAbove || offset > bestAbove.offset) {
        bestAbove = { el, offset }
      }
    } else if (!firstBelow) {
      firstBelow = { el, offset }
    }
  }

  const picked = bestAbove ?? firstBelow
  if (!picked || !picked.el.id) return null
  return { id: picked.el.id, offset: picked.offset }
}

/**
 * Scroll the container so that `el`'s top sits `scrollOffset` pixels below
 * the container's top (reproducing the saved view). `scrollOffset` is
 * typically negative, meaning the anchor is above the fold.
 */
function scrollToAnchor(container: HTMLElement, el: HTMLElement, scrollOffset: number): void {
  const containerTop = container.getBoundingClientRect().top
  const elTop = el.getBoundingClientRect().top
  const deltaY = elTop - containerTop - scrollOffset
  container.scrollTop += deltaY
}

export function useScrollBookmark({
  containerRef,
  conversationId,
  tabId,
  ready,
  firstVisit = 'top',
}: UseScrollBookmarkOptions): void {
  // Suppresses the scroll-save that our own programmatic restore triggers.
  // Flipped true for a single tick around the restore, then cleared.
  const suppressSaveRef = useRef(false)

  // Save a debounced bookmark while the user scrolls.
  useEffect(() => {
    if (!ready || !conversationId) return
    const container = containerRef.current
    if (!container) return

    let timer: number | null = null
    const onScroll = () => {
      if (suppressSaveRef.current) return
      if (timer !== null) window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        const snapshot = findTopAnchor(container)
        if (snapshot) saveBookmark(conversationId, tabId, snapshot.id, snapshot.offset)
      }, SCROLL_DEBOUNCE_MS)
    }

    container.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      if (timer !== null) window.clearTimeout(timer)
      container.removeEventListener('scroll', onScroll)
    }
  }, [containerRef, conversationId, tabId, ready])

  // Restore on mount / conversation change. No cleanup-save: the scroll
  // debounce has already captured the user's last position.
  useEffect(() => {
    if (!ready || !conversationId) return
    const container = containerRef.current
    if (!container) return

    suppressSaveRef.current = true

    const bookmark = getBookmark(conversationId, tabId)
    // rAF lets nested sections finish laying out (markdown, collapsibles)
    // before we measure positions.
    const rafId = window.requestAnimationFrame(() => {
      if (bookmark) {
        const el = container.querySelector<HTMLElement>(`#${CSS.escape(bookmark.anchorId)}`)
        if (el) {
          scrollToAnchor(container, el, bookmark.scrollOffset)
        } else if (firstVisit === 'bottom') {
          container.scrollTop = container.scrollHeight
        }
      } else if (firstVisit === 'bottom') {
        container.scrollTop = container.scrollHeight
      }
      // Drop the suppression one frame later so any scroll-event echo
      // of our programmatic scroll is ignored.
      window.requestAnimationFrame(() => {
        suppressSaveRef.current = false
      })
    })

    return () => {
      window.cancelAnimationFrame(rafId)
      suppressSaveRef.current = false
    }
  }, [containerRef, conversationId, tabId, ready, firstVisit])
}
